import DateInput from '../../../components/DateInput';
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../../lib/api';
import { safeConfirm } from '../../../lib/confirm';
import { Printer, Calculator, Banknote, Check, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Modal, L } from './ui';

/* ---------------- Payroll runs (iter 145) ---------------- */

function currentPeriod() {
  return new Date().toISOString().slice(0, 7);
}

function PayrollPanel({ emp }) {
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    period: currentPeriod(),
    gross_monthly: emp.monthly_salary || '',
    vacation_start: emp.vacation_start || '', vacation_end: emp.vacation_end || '',
    extra_vacation_dates: emp.extra_vacation_dates || '',
    sick_dates: emp.sick_dates || '', bereavement_dates: emp.bereavement_dates || '',
    maternity_start: emp.maternity_start || '', maternity_end: emp.maternity_end || '',
    payment_reference: emp.payment_reference || `Salary ${currentPeriod()}`,
  });
  const set = (k, v) => setForm(x => ({ ...x, [k]: v }));

  const load = () => api.get(`/hr/employees/${emp.employee_id}/payroll-runs`).then(r => setRuns(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, [emp.employee_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculate = async () => {
    setBusy(true);
    try {
      const payload = { ...form, gross_monthly: form.gross_monthly ? parseFloat(form.gross_monthly) : null };
      const { data } = await api.post(`/hr/employees/${emp.employee_id}/payroll-runs`, payload);
      setRuns(r => [data, ...r]);
      toast.success(`Payroll for ${data.period} calculated — previous calculations are preserved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Calculation failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="surface border border-soft rounded-lg p-4 print:hidden" data-testid="hr-payroll-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Banknote className="w-4 h-4 text-brand"/> Payroll — pay periods, vacation & remuneration</h3>
        <button className="btn-secondary text-xs" onClick={() => setOpen(o => !o)} data-testid="hr-payroll-toggle">{open ? 'Hide' : `Open (${runs.length} run${runs.length === 1 ? '' : 's'})`}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-4">
          <div className="border border-soft rounded-lg p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-fg-muted mb-2">New pay-period calculation</div>
            <div className="grid md:grid-cols-3 gap-3">
              <L label="Pay period (month)"><input className="input" type="month" value={form.period} onChange={e=>set('period', e.target.value)} data-testid="pr-period"/></L>
              <L label="Gross salary (defaults to HR record)"><input className="input" type="number" min="0" value={form.gross_monthly} onChange={e=>set('gross_monthly', e.target.value)} data-testid="pr-gross"/></L>
              <L label="Payment reference"><input className="input" value={form.payment_reference} onChange={e=>set('payment_reference', e.target.value)} data-testid="pr-reference"/></L>
              <L label="Vacation start"><DateInput value={form.vacation_start} onChange={e=>set('vacation_start', e.target.value)} data-testid="pr-vac-start"/></L>
              <L label="Vacation end"><DateInput value={form.vacation_end} onChange={e=>set('vacation_end', e.target.value)} data-testid="pr-vac-end"/></L>
              <L label="Extra / accidental vacation dates (comma-separated)"><input className="input" value={form.extra_vacation_dates} onChange={e=>set('extra_vacation_dates', e.target.value)} placeholder="2026-06-12, 2026-06-19" data-testid="pr-extra-vac"/></L>
              <L label="Sick dates (comma-separated)"><input className="input" value={form.sick_dates} onChange={e=>set('sick_dates', e.target.value)} placeholder="2026-06-03, 2026-06-04" data-testid="pr-sick"/></L>
              <L label="Bereavement dates (comma-separated)"><input className="input" value={form.bereavement_dates} onChange={e=>set('bereavement_dates', e.target.value)} data-testid="pr-bereavement"/></L>
              <div className="grid grid-cols-2 gap-2">
                <L label="Maternity start"><DateInput value={form.maternity_start} onChange={e=>set('maternity_start', e.target.value)} data-testid="pr-mat-start"/></L>
                <L label="Maternity end"><DateInput value={form.maternity_end} onChange={e=>set('maternity_end', e.target.value)} data-testid="pr-mat-end"/></L>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button className="btn-primary text-xs inline-flex items-center gap-1.5" disabled={busy} onClick={calculate} data-testid="pr-calculate-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Calculator className="w-3.5 h-3.5"/>}
                Calculate payroll estimate
              </button>
            </div>
          </div>

          {runs.length === 0
            ? <p className="text-xs text-fg-muted">No payroll runs yet. Each calculation is stored as its own snapshot — new runs never change previous ones.</p>
            : runs.map(run => <PayrollRun key={run.run_id} run={run} emp={emp} onChanged={load}/>)}
        </div>
      )}
    </div>
  );
}

function PayrollRun({ run, emp, onChanged }) {
  const [edit, setEdit] = useState({ net_payable: run.net_payable, payment_reference: run.payment_reference || '', notes: run.notes || '' });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const [showPay, setShowPay] = useState(false);
  const [showStub, setShowStub] = useState(false);
  const dirty = useRef(false);
  const first = useRef(true);
  const isDraft = run.status !== 'executed';

  // Autosave draft edits (debounced).
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (!isDraft) return;
    dirty.current = true;
    const t = setTimeout(async () => {
      if (!dirty.current) return;
      try {
        await api.put(`/hr/payroll-runs/${run.run_id}`, {
          net_payable: edit.net_payable === '' ? null : parseFloat(edit.net_payable),
          payment_reference: edit.payment_reference || null,
          notes: edit.notes || null,
        });
        dirty.current = false;
        setSaved(new Date().toLocaleTimeString());
      } catch { /* silent */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [edit]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async () => {
    if (!(await safeConfirm('Delete this draft payroll run?'))) return;
    try {
      await api.delete(`/hr/payroll-runs/${run.run_id}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const lv = run.leave || {};
  return (
    <div className={`border rounded-lg p-3 text-xs ${isDraft ? 'border-soft' : 'border-emerald-200 bg-emerald-50/30'}`} data-testid={`pr-run-${run.run_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-semibold flex items-center gap-2">
          Pay period {run.period}
          {isDraft
            ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">draft — editable</span>
            : <span className="badge-success text-[10px]">executed {String(run.executed_at || '').slice(0, 10)}</span>}
          {saved && isDraft && <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5"><Check className="w-3 h-3"/> autosaved {saved}</span>}
        </div>
        <div className="text-fg-muted">{run.gross_monthly?.toFixed(2)} {run.currency} gross · calculated {new Date(run.created_at).toLocaleString()}</div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-2">
        <div>
          <div className="text-fg-muted uppercase tracking-wide text-[10px] mb-1">Estimate ({run.estimate?.scheme})</div>
          {(run.estimate?.employee_lines || []).map((l, i) => (
            <div key={i} className="flex justify-between py-0.5"><span>{l.label}</span><span className={l.amount < 0 ? 'text-[#B83A3A]' : ''}>{l.amount.toFixed(2)}</span></div>
          ))}
          {(lv.lines || []).map((l, i) => (
            <div key={`lv${i}`} className="flex justify-between py-0.5 text-brand"><span>{l.label}</span><span className={l.amount < 0 ? 'text-[#B83A3A]' : ''}>{l.amount.toFixed(2)}</span></div>
          ))}
          <div className="flex justify-between font-bold border-t border-soft mt-1 pt-1"><span>Net estimated</span><span>{run.net_estimated?.toFixed(2)} {run.currency}</span></div>
        </div>
        <div>
          <div className="text-fg-muted uppercase tracking-wide text-[10px] mb-1">Leave & accrual this period</div>
          <div className="flex justify-between py-0.5"><span>Vacation days taken</span><span>{lv.vacation_days || 0}</span></div>
          <div className="flex justify-between py-0.5"><span>Sick days</span><span>{lv.sick_days || 0}</span></div>
          <div className="flex justify-between py-0.5"><span>Bereavement days</span><span>{lv.bereavement_days || 0}</span></div>
          <div className="flex justify-between py-0.5"><span>Maternity days</span><span>{lv.maternity_days || 0}</span></div>
          <div className="flex justify-between py-0.5 font-medium"><span>Vacation accrued this period</span><span data-testid={`pr-accrual-${run.run_id}`}>{run.vacation_accrued_this_period} day(s)</span></div>
          <div className="flex justify-between py-0.5 text-fg-muted"><span>Daily rate</span><span>{run.daily_rate?.toFixed(2)} {run.currency}</span></div>

          <div className="grid grid-cols-2 gap-2 mt-2">
            <L label="Net payable (editable)">
              <input className="input !py-1 text-xs" type="number" step="0.01" disabled={!isDraft}
                     value={edit.net_payable ?? ''} onChange={e => setEdit(x => ({ ...x, net_payable: e.target.value }))}
                     data-testid={`pr-net-${run.run_id}`}/>
            </L>
            <L label="Payment reference">
              <input className="input !py-1 text-xs" disabled={!isDraft}
                     value={edit.payment_reference} onChange={e => setEdit(x => ({ ...x, payment_reference: e.target.value }))}
                     data-testid={`pr-ref-${run.run_id}`}/>
            </L>
          </div>
        </div>
      </div>

      {isDraft ? (
        <div className="flex justify-end gap-2 mt-2 items-center">
          <button className="text-[#B83A3A] text-xs hover:opacity-70" onClick={del} data-testid={`pr-delete-${run.run_id}`}>Delete draft</button>
          <button className="btn-secondary !py-1 text-xs inline-flex items-center gap-1" onClick={() => setShowStub(true)} data-testid={`pr-stub-${run.run_id}`}>
            <Printer className="w-3 h-3"/> Pay stub
          </button>
          <button className="btn-primary !py-1 text-xs inline-flex items-center gap-1" disabled={busy} onClick={() => setShowPay(true)} data-testid={`pr-execute-${run.run_id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>}
            Process payment & send pay stub
          </button>
        </div>
      ) : (
        <div className="flex justify-end gap-2 mt-2 items-center">
          {run.payment_method && (
            <span className="text-[10px] text-fg-muted">Paid via {(run.payment_method || '').replace('_', ' ')}{run.payment_destination ? ` → ${run.payment_destination}` : ''}</span>
          )}
          <button className="btn-secondary !py-1 text-xs inline-flex items-center gap-1" onClick={() => setShowStub(true)} data-testid={`pr-stub-${run.run_id}`}>
            <Printer className="w-3 h-3"/> Pay stub
          </button>
        </div>
      )}
      {showPay && <PaymentModal run={{ ...run, net_payable: edit.net_payable }} emp={emp}
                                onClose={() => setShowPay(false)}
                                onDone={() => { setShowPay(false); onChanged(); }}/>}
      {showStub && <PayStubModal run={run} emp={emp} onClose={() => setShowStub(false)} onChanged={onChanged}/>}
    </div>
  );
}

/* ---------------- Payment processing (iter 146) ---------------- */

const PAY_METHODS = [
  { key: 'bank_transfer', label: 'Bank transfer — assigned bank account' },
  { key: 'credit_card', label: 'Credit card deposit' },
  { key: 'blik', label: 'BLIK phone transfer' },
];

function payDestination(emp, method) {
  if (!emp) return '';
  if (method === 'blik') return emp.phone || '';
  if (method === 'credit_card') return emp.bank_account_number || emp.bank_iban || '';
  return emp.bank_iban || emp.bank_account_number || emp.bank_name_branch || '';
}

function PaymentModal({ run, emp, onClose, onDone }) {
  const [method, setMethod] = useState('bank_transfer');
  const [destination, setDestination] = useState(payDestination(emp, 'bank_transfer'));
  const [busy, setBusy] = useState(false);

  const pick = (m) => { setMethod(m); setDestination(payDestination(emp, m)); };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hr/payroll-runs/${run.run_id}/execute`,
        { payment_method: method, destination });
      toast.success(data.pay_stub_sent
        ? 'Payment processed — pay stub emailed to the staff member.'
        : 'Payment processed. (No email on file — pay stub not sent.)');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payment failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center pt-24 px-4" onClick={onClose} data-testid="payment-modal">
      <div className="bg-white border border-soft rounded-xl w-full max-w-md shadow-xl p-5" onClick={e => e.stopPropagation()}>
        <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><Send className="w-4 h-4 text-brand"/> Process salary payment</h3>
        <p className="text-xs text-fg-muted mb-3">
          {run.employee_name} · {run.period} · <strong>{Number(run.net_payable).toFixed(2)} {run.currency}</strong> net.
          The run is locked after payment and the pay stub is emailed.
        </p>
        <div className="space-y-2 mb-3">
          {PAY_METHODS.map(m => (
            <label key={m.key} className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer text-sm ${method === m.key ? 'border-brand bg-brand/5' : 'border-soft'}`} data-testid={`pay-method-${m.key}`}>
              <input type="radio" name="pay-method" checked={method === m.key} onChange={() => pick(m.key)}/>
              {m.label}
            </label>
          ))}
        </div>
        <label className="block mb-4">
          <div className="text-xs text-fg-muted mb-1">
            {method === 'blik' ? 'Phone number (BLIK)' : method === 'credit_card' ? 'Card / account reference' : 'Bank account (IBAN / account number)'}
            {' '}— auto-filled from the staff record
          </div>
          <input className="input font-mono" value={destination} onChange={e => setDestination(e.target.value)} data-testid="pay-destination"/>
        </label>
        <p className="text-[10px] text-fg-muted mb-3">This records the payment and issues the pay stub. Complete the actual transfer in your banking app using these details.</p>
        <div className="flex justify-end gap-2">
          <button className="btn-secondary text-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary text-sm inline-flex items-center gap-1.5" disabled={busy} onClick={confirm} data-testid="pay-confirm-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin"/>} Confirm payment
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Pay stub (iter 146) ---------------- */

function PayStubModal({ run: initialRun, emp, onClose, onChanged }) {
  const [run, setRun] = useState(initialRun);
  const [clinic, setClinic] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const isDraft = run.status !== 'executed';
  const lv = run.leave || {};

  useEffect(() => {
    api.get(`/hr/employees/${emp.employee_id}/business-card`).then(r => setClinic(r.data)).catch(() => {});
  }, [emp.employee_id]);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/hr/payroll-runs/${run.run_id}`);
      setRun(data);
      onChanged?.();
    } catch { /* noop */ }
  };

  const lines = [
    ...(run.estimate?.employee_lines || []),
    ...(lv.lines || []),
    ...(run.adjustments || []).map(a => ({ label: a.label, amount: Number(a.amount) })),
  ];

  const print = () => {
    const w = window.open('', '_blank', 'width=800,height=900');
    const rows = lines.map(l =>
      `<tr><td style="padding:4px 10px">${l.label}</td><td style="padding:4px 10px;text-align:right">${Number(l.amount).toFixed(2)}</td></tr>`).join('');
    w.document.write(`<!doctype html><html><head><title>Pay stub ${run.period}</title>
      <style>body{font-family:Arial,sans-serif;color:#1a1a1a;max-width:640px;margin:36px auto;padding:0 20px}
      h1{font-size:18px;color:#003D80;border-bottom:3px solid #003D80;padding-bottom:8px}
      table{border-collapse:collapse;width:100%;font-size:13px;margin-top:10px}
      .tot td{font-weight:bold;border-top:2px solid #003D80;padding:8px 10px}
      .meta{font-size:12px;color:#555;line-height:1.7}
      .draft{color:#B45309;font-weight:bold}</style></head><body>
      <h1>${clinic?.clinic_name || ''} — Pay stub · ${run.period}</h1>
      <div class="meta">
        <div><strong>${run.employee_name}</strong>${clinic?.title ? ' · ' + clinic.title : ''}</div>
        <div>Gross monthly: ${Number(run.gross_monthly).toFixed(2)} ${run.currency} · Scheme: ${run.estimate?.scheme || ''} (${run.country})</div>
        <div>Reference: ${run.payment_reference || 'Salary'} · ${isDraft ? '<span class="draft">DRAFT — payment not yet processed</span>' : `Paid ${String(run.executed_at || '').slice(0, 10)} via ${(run.payment_method || 'bank transfer').replace('_', ' ')}${run.payment_destination ? ' → ' + run.payment_destination : ''}`}</div>
        <div>Leave this period: vacation ${lv.vacation_days || 0} · sick ${lv.sick_days || 0} · bereavement ${lv.bereavement_days || 0} · maternity ${lv.maternity_days || 0} day(s) · Vacation accrued: ${run.vacation_accrued_this_period || 0} day(s)</div>
      </div>
      <table>${rows}<tr class="tot"><td>NET PAY</td><td style="text-align:right">${Number(run.net_payable).toFixed(2)} ${run.currency}</td></tr></table>
      <p style="font-size:10px;color:#888;margin-top:18px">Figures are estimates until confirmed by your accountant. Payroll records must be retained per statutory law (e.g. 10 years in Poland for post-2019 hires). Banking details are processed under GDPR Art. 6(1)(b) for contract performance.</p>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  return (
    <Modal onClose={onClose}>
      <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><Banknote className="w-5 h-5 text-brand"/> Pay stub — {run.period}</h3>
      <p className="text-xs text-fg-muted mb-3">
        {run.employee_name} · auto-filled from the staff record.
        {isDraft ? ' This is a DRAFT — process the payment to lock it and email the stub.' : ` Paid ${String(run.executed_at || '').slice(0, 10)}.`}
      </p>
      <div className="border border-soft rounded-lg p-4 text-xs" data-testid="paystub-preview">
        <div className="flex justify-between font-semibold border-b border-soft pb-2 mb-2">
          <span>{clinic?.clinic_name || '…'}</span>
          <span>{run.period}{isDraft ? ' · DRAFT' : ''}</span>
        </div>
        <div className="flex justify-between py-0.5"><span>Gross monthly</span><span>{Number(run.gross_monthly).toFixed(2)} {run.currency}</span></div>
        {lines.map((l, i) => (
          <div key={i} className="flex justify-between py-0.5"><span>{l.label}</span><span className={l.amount < 0 ? 'text-[#B83A3A]' : ''}>{Number(l.amount).toFixed(2)}</span></div>
        ))}
        <div className="flex justify-between font-bold border-t border-soft mt-1 pt-1.5"><span>NET PAY</span><span data-testid="paystub-net">{Number(run.net_payable).toFixed(2)} {run.currency}</span></div>
        <div className="text-fg-muted mt-2">
          Reference: {run.payment_reference || 'Salary'} · Vacation accrued: {run.vacation_accrued_this_period || 0} day(s)
          {!isDraft && <> · Paid via {(run.payment_method || 'bank transfer').replace('_', ' ')}{run.payment_destination ? ` → ${run.payment_destination}` : ''}</>}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button className="btn-secondary text-xs" onClick={onClose}>Close</button>
        <button className="btn-secondary text-xs inline-flex items-center gap-1.5" onClick={print} data-testid="paystub-print-btn">
          <Printer className="w-3.5 h-3.5"/> Print pay stub
        </button>
        {isDraft && (
          <button className="btn-primary text-xs inline-flex items-center gap-1.5" onClick={() => setShowPay(true)} data-testid="paystub-pay-btn">
            <Send className="w-3.5 h-3.5"/> Process payment
          </button>
        )}
      </div>
      {showPay && <PaymentModal run={run} emp={emp} onClose={() => setShowPay(false)}
                                onDone={() => { setShowPay(false); refresh(); }}/>}
    </Modal>
  );
}

export { currentPeriod, PayrollPanel, PayrollRun, PAY_METHODS, payDestination, PaymentModal, PayStubModal };
